import React, { useState, useEffect } from 'react';
import { 
  X, 
  Save, 
  Building2, 
  Edit3, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  User,
  Mail,
  Phone,
  MapPin,
  Landmark,
  CreditCard
} from 'lucide-react';
import { supplierService } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Moved outside to prevent re-creation on every render
function InfoRow({ icon: Icon, label, value, name, type = 'text', options = null, isEditing, formData, handleChange }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        fontWeight: '600',
        color: '#64748b',
        marginBottom: '6px',
      }}>
        <Icon size={14} />
        {label}
      </label>
      {isEditing ? (
        options ? (
          <select
            name={name}
            value={formData[name]}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          >
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            name={name}
            value={formData[name]}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
        )
      ) : (
        <p style={{
          margin: 0,
          fontSize: '15px',
          color: '#1e293b',
          padding: '10px 0',
        }}>
          {value || '-'}
        </p>
      )}
    </div>
  );
}

function SupplierDetail({ supplier, onClose, onUpdate }) {
  const { canApproveSuppliers } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [fullSupplier, setFullSupplier] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(true);
  
  const [formData, setFormData] = useState({
    supplier_name: '',
    service_category: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    bank_name: '',
    account_holder: '',
    account_number: '',
    branch_code: '',
    swift_code: '',
    currency: '',
  });

  useEffect(() => {
    const fetchFullDetails = async () => {
      try {
        setLoadingDetails(true);
        const response = await supplierService.getById(supplier.id);
        const data = response.data;
        setFullSupplier(data);
        setFormData({
          supplier_name: data.supplier_name || '',
          service_category: data.service_category || '',
          contact_person: data.contact_person || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          city: data.city || '',
          country: data.country || '',
          bank_name: data.bank_name || '',
          account_holder: data.account_holder || '',
          account_number: data.account_number || '',
          branch_code: data.branch_code || '',
          swift_code: data.swift_code || '',
          currency: data.currency || '',
        });
      } catch (err) {
        console.error('Error fetching supplier details:', err);
        setError('Failed to load supplier details');
      } finally {
        setLoadingDetails(false);
      }
    };

    if (supplier?.id) {
      fetchFullDetails();
    }
  }, [supplier]);

  const SERVICE_CATEGORIES = [
    { value: 'CONSULTING', label: 'Consulting' },
    { value: 'IT_SERVICES', label: 'IT Services' },
    { value: 'MARKETING', label: 'Marketing' },
    { value: 'LEGAL', label: 'Legal' },
    { value: 'ACCOUNTING', label: 'Accounting' },
    { value: 'FACILITIES', label: 'Facilities' },
    { value: 'OTHER', label: 'Other' },
  ];

  const CURRENCIES = [
    { value: 'ZAR', label: 'ZAR - South African Rand' },
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'AUD', label: 'AUD - Australian Dollar' },
    { value: 'NZD', label: 'NZD - New Zealand Dollar' },
    { value: 'MUR', label: 'MUR - Mauritian Rupee' },
    { value: 'CNY', label: 'CNY - Chinese Yuan' },
    { value: 'JPY', label: 'JPY - Japanese Yen' },
    { value: 'HKD', label: 'HKD - Hong Kong Dollar' },
    { value: 'SGD', label: 'SGD - Singapore Dollar' },
    { value: 'INR', label: 'INR - Indian Rupee' },
    { value: 'AED', label: 'AED - UAE Dirham' },
    { value: 'SAR', label: 'SAR - Saudi Riyal' },
    { value: 'KES', label: 'KES - Kenyan Shilling' },
    { value: 'NGN', label: 'NGN - Nigerian Naira' },
    { value: 'CAD', label: 'CAD - Canadian Dollar' },
    { value: 'CHF', label: 'CHF - Swiss Franc' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const response = await supplierService.update(supplier.id, formData);
      setFullSupplier(response.data);
      setSuccessMessage('Supplier updated successfully!');
      setIsEditing(false);
      onUpdate();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating supplier:', err);
      if (err.response?.data) {
        const errors = err.response.data;
        const errorMessages = Object.entries(errors)
          .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
          .join('\n');
        setError(errorMessages);
      } else {
        setError('Failed to update supplier. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setLoading(true);
      const response = await supplierService.approve(supplier.id);
      setFullSupplier(response.data.supplier);
      setSuccessMessage('Supplier approved!');
      onUpdate();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to approve supplier');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    const reason = window.prompt('Please enter a rejection reason:');
    if (reason === null) return;
    if (!reason.trim()) {
      setError('Rejection reason is required');
      return;
    }
    
    try {
      setLoading(true);
      const response = await supplierService.reject(supplier.id, reason);
      setFullSupplier(response.data.supplier);
      setSuccessMessage('Supplier rejected');
      onUpdate();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to reject supplier');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      APPROVED: { bg: '#dcfce7', color: '#166534', icon: CheckCircle, label: 'Approved' },
      REJECTED: { bg: '#fee2e2', color: '#991b1b', icon: XCircle, label: 'Rejected' },
      PENDING: { bg: '#fef3c7', color: '#92400e', icon: AlertCircle, label: 'Pending Approval' },
    };
    const style = styles[status] || styles.PENDING;
    const Icon = style.icon;

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 14px',
        borderRadius: '9999px',
        fontSize: '13px',
        fontWeight: '500',
        backgroundColor: style.bg,
        color: style.color,
      }}>
        <Icon size={16} />
        {style.label}
      </span>
    );
  };

  if (loadingDetails) {
    return (
      <div style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}>
        <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px' }}>
          Loading supplier details...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      padding: '40px 20px',
      overflowY: 'auto',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '700px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          borderBottom: '1px solid #e2e8f0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '44px',
              height: '44px',
              backgroundColor: '#dbeafe',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Building2 size={22} color="#3b82f6" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                {isEditing ? 'Edit Supplier' : 'Supplier Details'}
              </h2>
              <StatusBadge status={fullSupplier?.approval_status} />
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
            }}
          >
            <X size={20} color="#64748b" />
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div style={{
            margin: '16px 24px 0',
            padding: '12px 16px',
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            borderRadius: '8px',
            fontSize: '14px',
            whiteSpace: 'pre-line',
          }}>
            {error}
          </div>
        )}
        {successMessage && (
          <div style={{
            margin: '16px 24px 0',
            padding: '12px 16px',
            backgroundColor: '#dcfce7',
            color: '#166534',
            borderRadius: '8px',
            fontSize: '14px',
          }}>
            {successMessage}
          </div>
        )}

        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {/* Basic Information */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#1e293b',
              marginBottom: '16px',
              paddingBottom: '8px',
              borderBottom: '1px solid #e2e8f0',
            }}>
              Basic Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
              <div style={{ gridColumn: 'span 2' }}>
                <InfoRow 
                  icon={Building2} 
                  label="Supplier Name" 
                  value={fullSupplier?.supplier_name}
                  name="supplier_name"
                  isEditing={isEditing}
                  formData={formData}
                  handleChange={handleChange}
                />
              </div>
              <InfoRow 
                icon={Building2} 
                label="Service Category" 
                value={SERVICE_CATEGORIES.find(c => c.value === fullSupplier?.service_category)?.label}
                name="service_category"
                options={SERVICE_CATEGORIES}
                isEditing={isEditing}
                formData={formData}
                handleChange={handleChange}
              />
              <InfoRow 
                icon={User} 
                label="Contact Person" 
                value={fullSupplier?.contact_person}
                name="contact_person"
                isEditing={isEditing}
                formData={formData}
                handleChange={handleChange}
              />
              <InfoRow 
                icon={Mail} 
                label="Email" 
                value={fullSupplier?.email}
                name="email"
                type="email"
                isEditing={isEditing}
                formData={formData}
                handleChange={handleChange}
              />
              <InfoRow 
                icon={Phone} 
                label="Phone" 
                value={fullSupplier?.phone}
                name="phone"
                isEditing={isEditing}
                formData={formData}
                handleChange={handleChange}
              />
            </div>
          </div>

          {/* Location */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#1e293b',
              marginBottom: '16px',
              paddingBottom: '8px',
              borderBottom: '1px solid #e2e8f0',
            }}>
              Location
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
              <div style={{ gridColumn: 'span 2' }}>
                <InfoRow 
                  icon={MapPin} 
                  label="Address" 
                  value={fullSupplier?.address}
                  name="address"
                  isEditing={isEditing}
                  formData={formData}
                  handleChange={handleChange}
                />
              </div>
              <InfoRow 
                icon={MapPin} 
                label="City" 
                value={fullSupplier?.city}
                name="city"
                isEditing={isEditing}
                formData={formData}
                handleChange={handleChange}
              />
              <InfoRow 
                icon={MapPin} 
                label="Country" 
                value={fullSupplier?.country}
                name="country"
                isEditing={isEditing}
                formData={formData}
                handleChange={handleChange}
              />
            </div>
          </div>

          {/* Banking Details */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#1e293b',
              marginBottom: '16px',
              paddingBottom: '8px',
              borderBottom: '1px solid #e2e8f0',
            }}>
              Banking Details
              {isEditing && fullSupplier?.approval_status === 'APPROVED' && (
                <span style={{
                  marginLeft: '12px',
                  fontSize: '11px',
                  color: '#f59e0b',
                  fontWeight: '400',
                }}>
                  ⚠️ Changing banking details will reset approval status
                </span>
              )}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
              <InfoRow 
                icon={Landmark} 
                label="Bank Name" 
                value={fullSupplier?.bank_name}
                name="bank_name"
                isEditing={isEditing}
                formData={formData}
                handleChange={handleChange}
              />
              <InfoRow 
                icon={User} 
                label="Account Holder" 
                value={fullSupplier?.account_holder}
                name="account_holder"
                isEditing={isEditing}
                formData={formData}
                handleChange={handleChange}
              />
              <InfoRow 
                icon={CreditCard} 
                label="Account Number" 
                value={fullSupplier?.account_number}
                name="account_number"
                isEditing={isEditing}
                formData={formData}
                handleChange={handleChange}
              />
              <InfoRow 
                icon={Landmark} 
                label="Branch Code" 
                value={fullSupplier?.branch_code}
                name="branch_code"
                isEditing={isEditing}
                formData={formData}
                handleChange={handleChange}
              />
              <InfoRow 
                icon={Landmark} 
                label="SWIFT Code" 
                value={fullSupplier?.swift_code}
                name="swift_code"
                isEditing={isEditing}
                formData={formData}
                handleChange={handleChange}
              />
              <InfoRow 
                icon={CreditCard} 
                label="Currency" 
                value={CURRENCIES.find(c => c.value === fullSupplier?.currency)?.label}
                name="currency"
                options={CURRENCIES}
                isEditing={isEditing}
                formData={formData}
                handleChange={handleChange}
              />
            </div>
          </div>

          {/* Rejection Reason */}
          {fullSupplier?.approval_status === 'REJECTED' && fullSupplier?.rejection_reason && (
            <div style={{
              padding: '16px',
              backgroundColor: '#fee2e2',
              borderRadius: '8px',
              marginBottom: '24px',
            }}>
              <p style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#991b1b' }}>
                Rejection Reason:
              </p>
              <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#991b1b' }}>
                {fullSupplier?.rejection_reason}
              </p>
            </div>
          )}

          {/* Audit Info */}
          <div style={{
            padding: '16px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#64748b',
          }}>
            <p style={{ margin: '0 0 4px' }}>
              Created by {fullSupplier?.created_by?.username || 'Unknown'} on {fullSupplier?.created_at ? new Date(fullSupplier.created_at).toLocaleString() : '-'}
            </p>
            {fullSupplier?.approved_by && (
              <p style={{ margin: 0 }}>
                {fullSupplier?.approval_status === 'APPROVED' ? 'Approved' : 'Reviewed'} by {fullSupplier?.approved_by?.username} on {fullSupplier?.approved_at ? new Date(fullSupplier.approved_at).toLocaleString() : '-'}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '16px 24px',
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
        }}>
          {/* Approval Actions */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {fullSupplier?.approval_status === 'PENDING' && !isEditing && canApproveSuppliers() && (
              <>
                <button
                  onClick={handleApprove}
                  disabled={loading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 16px',
                    backgroundColor: '#22c55e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  <CheckCircle size={16} />
                  Approve
                </button>
                <button
                  onClick={handleReject}
                  disabled={loading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 16px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  <XCircle size={16} />
                  Reject
                </button>
              </>
            )}
          </div>

          {/* Edit/Save Actions */}
          <div style={{ display: 'flex', gap: '12px' }}>
            {isEditing ? (
              <>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setError('');
                    if (fullSupplier) {
                      setFormData({
                        supplier_name: fullSupplier.supplier_name || '',
                        service_category: fullSupplier.service_category || '',
                        contact_person: fullSupplier.contact_person || '',
                        email: fullSupplier.email || '',
                        phone: fullSupplier.phone || '',
                        address: fullSupplier.address || '',
                        city: fullSupplier.city || '',
                        country: fullSupplier.country || '',
                        bank_name: fullSupplier.bank_name || '',
                        account_holder: fullSupplier.account_holder || '',
                        account_number: fullSupplier.account_number || '',
                        branch_code: fullSupplier.branch_code || '',
                        swift_code: fullSupplier.swift_code || '',
                        currency: fullSupplier.currency || '',
                      });
                    }
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'white',
                    color: '#475569',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    backgroundColor: loading ? '#93c5fd' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Save size={16} />
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                <Edit3 size={16} />
                Edit Supplier
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SupplierDetail;